import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Percent } from 'lucide-react';
import { useDeductibleVat } from '../hooks/useDeductibleVat.js';
import TransactionRow from '../components/TransactionRow.jsx';
import TransactionDetailSlideOver from '../components/TransactionDetailSlideOver.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import { formatCurrency, formatMonth } from '../lib/format.js';

function currentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function shiftPeriod(period, deltaMonths) {
  const [y, m] = period.split('-').map(Number);
  const d = new Date(y, m - 1 + deltaMonths, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function periodLabel(period) {
  const [y, m] = period.split('-').map(Number);
  return formatMonth(y, m);
}

export default function VatDeductiblePage() {
  const [period, setPeriod] = useState(currentPeriod);
  const { data, isLoading, error } = useDeductibleVat(period);

  const [selectedId, setSelectedId] = useState(null);
  const transactions = data?.transactions ?? [];
  const selected = useMemo(
    () => (selectedId ? transactions.find((t) => t.id === selectedId) ?? null : null),
    [selectedId, transactions]
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-lg font-semibold text-slate-900">TVA déductible</h1>
        <p className="text-sm text-slate-500">Préparation de la déclaration mensuelle</p>
      </div>

      {/* Period nav */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setPeriod((p) => shiftPeriod(p, -1))}
          className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors"
          aria-label="Mois précédent"
        >
          <ChevronLeft size={18} />
        </button>
        <span className="text-sm font-semibold text-slate-800 min-w-[160px] text-center capitalize">
          {periodLabel(period)}
        </span>
        <button
          type="button"
          onClick={() => setPeriod((p) => shiftPeriod(p, 1))}
          className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors"
          aria-label="Mois suivant"
        >
          <ChevronRight size={18} />
        </button>
        {period !== currentPeriod() && (
          <button
            type="button"
            onClick={() => setPeriod(currentPeriod())}
            className="ml-1 text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2"
          >
            Mois courant
          </button>
        )}
      </div>

      {/* KPI card */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-slate-100 rounded-lg shrink-0">
            <Percent size={18} className="text-slate-600" aria-hidden />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">
              Total TVA déductible
            </p>
            <p className="text-3xl font-bold text-slate-900 tabular-nums mt-0.5">
              {isLoading ? '—' : formatCurrency(data?.total_deductible_cents ?? 0)}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {isLoading
                ? 'Chargement…'
                : `${data?.transaction_count ?? 0} transaction${(data?.transaction_count ?? 0) > 1 ? 's' : ''} contributrice${(data?.transaction_count ?? 0) > 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
      </div>

      {/* Transactions list */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {/* Header desktop */}
        <div className="hidden md:grid grid-cols-[40px_100px_minmax(180px,1.5fr)_minmax(160px,1fr)_140px] gap-4 items-center px-4 py-2 border-b border-slate-200 bg-slate-50 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
          <div></div>
          <div>Date</div>
          <div>Fournisseur</div>
          <div>Catégorie</div>
          <div className="text-right">Montant</div>
        </div>

        {isLoading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        )}

        {error && !isLoading && (
          <div className="px-4 py-8 text-center">
            <p className="text-sm text-red-600">Impossible de charger les données.</p>
          </div>
        )}

        {!isLoading && !error && transactions.length === 0 && (
          <div className="px-4 py-10 text-center">
            <p className="text-sm text-slate-500">
              Aucune TVA déductible sur cette période.
            </p>
          </div>
        )}

        {!isLoading && !error && transactions.length > 0 && (
          <div className="divide-y divide-slate-100">
            {transactions.map((tx) => (
              <TransactionRow
                key={tx.id}
                transaction={tx}
                onClick={(t) => setSelectedId(t.id)}
              />
            ))}
          </div>
        )}
      </div>

      <TransactionDetailSlideOver
        transaction={selected}
        open={!!selected}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}
